import React, { useCallback, useEffect, useState } from "react";
import WasteDisImg from "../../../img/app/img1.jpeg";
import AvatarImg from "../../../img/app/avatar_default_th.jpg";
import DefaultImage from "../../../img/app/default-Image.webp";
import moment from "moment-timezone";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { toScreen } from "../../../js/modules/Routing";
import { useLoader } from "../../../hooks/useLoader";
import { adminSaveChangesConfirm } from "../../../config/messages";
import { defaultAvatarImage, imageFormat, jobImagesFolder, serverUrl, thumbSuffix, userImagesFolder } from "../../../config/env";
import { defaultMessagesFilter } from "../../../config/constants";
import {
  addJobNotes,
  getJobNotes,
  refundPaymentDecision,
} from "../../../services/issueService";

const getJobTypeDisplay = (jobType) => {
  const jobTypeMap = {
    getitems: "Get items",
    housemove: "House move",
    senditems: "Send items",
    wasteDisposal: "Waste Disposal",
  };
  return jobTypeMap[jobType] || jobType;
};

const RefundDetails = ({
  onClose,
  selectedIssueResolution,
  conversationDetails,
  theCallBackFunc,
  userDetails,
  getConversations,
}) => {
  const { showLoader, hideLoader } = useLoader();
  const [activeTab, setActiveTab] = useState("chat");
  const [notes, setNotes] = useState([]);
  const [noteInput, setNoteInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [isDecisionMade, setIsDecisionMade] = useState(false);

  const navigate = useNavigate();

  const statusMap = React.useMemo(
    () => ({
      exempt: {
        label: "Waived",
        badge: "badge-secondary",
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-circle-off lucide-icon h-3 w-3"
          >
            <path d="m2 2 20 20"></path>
            <path d="M8.35 2.69A10 10 0 0 1 21.3 15.65"></path>
            <path d="M19.08 19.08A10 10 0 1 1 4.92 4.92"></path>
          </svg>
        ),
      },
      "not exempt": {
        label: "Applied",
        badge: "badge-danger",
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-circle-arrow-up lucide-icon h-3 w-3"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <path d="m16 12-4-4-4 4"></path>
            <path d="M12 16V8"></path>
          </svg>
        ),
      },
      pending: {
        label: "Pending",
        badge: "badge-warning",
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-clock h-3 w-3"
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        ),
      },
      approved: {
        label: "Approved",
        badge: "badge-success",
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-check h-3 w-3"
          >
            <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
            <path d="m9 11 3 3L22 4"></path>
          </svg>
        ),
      },
      rejected: {
        label: "Rejected",
        badge: "badge-danger",
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-x h-3 w-3"
          >
            <path d="M18 6 6 18"></path>
            <path d="m6 6 12 12"></path>
          </svg>
        ),
      },
    }),
    []
  );

  const jobStatus = React.useMemo(
    () => ({
      paid: {
        label: "Paid",
        badge: "badge-success",
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-circle-check-big lucide-icon h-3 w-3"
          >
            <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
            <path d="m9 11 3 3L22 4"></path>
          </svg>
        ),
      },
      closed: {
        label: "Cancelled",
        badge: "badge-danger",
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-x h-3 w-3"
          >
            <path d="M18 6 6 18"></path>
            <path d="m6 6 12 12"></path>
          </svg>
        ),
      },
    }),
    []
  );

  // Memoized computed values
  const status = selectedIssueResolution?.refund;
  const statusData = statusMap[status?.toLowerCase()];
  const jobDetailsStatus = selectedIssueResolution?.jobDetails?.status;
  const jobStatusData = jobStatus[jobDetailsStatus?.toLowerCase()];
  const INITIAL_NOTES = [];

  // Fixed getJobNotes function
  const handleGetJobNotes = useCallback(async () => {
    if (!selectedIssueResolution?.jobDetails?._id) {
      setNotes(INITIAL_NOTES);
      return;
    }

    setIsLoading(true);
    showLoader('Loading notes...');

    const queryParams = {
      noteType: "job",
      id: selectedIssueResolution?.jobDetails?._id,
    };

    try {
      const resJson = await getJobNotes(queryParams);

      hideLoader();
      setIsLoading(false);

      setNotes(resJson?.notes || INITIAL_NOTES);
    } catch (error) {
      hideLoader();
      setIsLoading(false);
      if (error.response?.status === 400) {
        toast.error(error.message);
      } else {
        toast.error(error.message || "Failed to fetch job notes");
      }
      setNotes(INITIAL_NOTES);
    }
  }, [selectedIssueResolution?.jobDetails?._id, showLoader, hideLoader]);

  // Add job notes with proper error handling
  const handleAddJobNotes = useCallback(
    async ({ id, content, noteType} ) => {
      const trimmedContent = content?.trim();
      if (trimmedContent) {
        showLoader('Adding note...');

        const payload = {
          id: selectedIssueResolution?.jobDetails?._id,
          content: trimmedContent,
          noteType: "job",
        };

        try {
          const resJson = await addJobNotes(payload);

          hideLoader();
          setIsAddingNote(false);

          toast.success("Note added successfully");
          setNoteInput("");
          handleGetJobNotes();
        } catch (error) {
          hideLoader();
          setIsAddingNote(false);
          if (error.response?.status === 400) {
            toast.error(error.message);
          } else {
            toast.error(error.message || "Failed to add note");
          }
        }
      }
    },
    [selectedIssueResolution?.jobDetails?._id, theCallBackFunc, handleGetJobNotes, showLoader, hideLoader]
  );

  // Fixed refund payment decision function
  const handleFetchRefundPayment = useCallback(
    async ({ paymentChargeId, decision }) => {
      if (!paymentChargeId) {
        toast.error("Payment charge ID not found");
        return;
      }

      showLoader(`${decision === 'approved' ? 'Approving' : 'Rejecting'} refund...`);

      const payload = {
        decision,
        paymentChargeId,
      };

      try {
        const { resStatus, responseString, resJson } = await refundPaymentDecision(payload);

        hideLoader();

        if (resStatus === 200) {
          toast.success("Refund Payment Decision has been resolved");
          if (theCallBackFunc) {
            theCallBackFunc();
          }
          if (onClose) {
            onClose();
          }
        } else if (resStatus === 400) {
          toast.error(responseString);
        } else {
          toast.error(responseString || "Something went wrong!");
        }
      } catch (error) {
        hideLoader();
        toast.error("Failed to process refund decision");
      }
    },
    [theCallBackFunc, onClose, showLoader, hideLoader]
  );

  // Memoized avatar functions
  const getDriverAvatarSrc = useCallback((selectedIssueResolution) => {
    if (
      selectedIssueResolution?.driverDetails?.avatar &&
      selectedIssueResolution?.driverDetails.avatar !== ""
    ) {
      return `${serverUrl}${userImagesFolder}/${selectedIssueResolution?.driverDetails?.shortId}/${selectedIssueResolution?.driverDetails?.avatar}${thumbSuffix}${imageFormat}`;
    }
    return `../img/${defaultAvatarImage}${imageFormat}`;
  }, []);

  const getClientAvatarSrc = useCallback((selectedIssueResolution) => {
    if (
      selectedIssueResolution?.clientDetails?.avatar &&
      selectedIssueResolution?.clientDetails.avatar !== ""
    ) {
      return `${serverUrl}${userImagesFolder}/${selectedIssueResolution?.clientDetails?.shortId}/${selectedIssueResolution?.clientDetails?.avatar}${thumbSuffix}${imageFormat}`;
    }
    return `../img/${defaultAvatarImage}${imageFormat}`;
  }, []);

  const getJobDetailsSrc = useCallback((shortId, image) => {
    if (image && shortId) {
      return `${serverUrl}${jobImagesFolder}/${shortId}/${image}${thumbSuffix}${imageFormat}`;
    }
    return DefaultImage;
  }, []);

  // Handle keyboard navigation for tabs
  const handleTabKeyDown = useCallback((e, tabName) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setActiveTab(tabName);
    }
  }, []);

  useEffect(() => {
    setIsDecisionMade(false);
  }, [selectedIssueResolution]); // or modal open trigger

  // Render loading state
  if (isLoading && !selectedIssueResolution) {
    return (
      <div className="right-modal">
        <div className="modal-header">
          <div className="modal-title">
            <h2>Refund Details</h2>
            <button className="modal-close-btn" onClick={onClose}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-x h-5 w-5"
              >
                <path d="M18 6 6 18"></path>
                <path d="m6 6 12 12"></path>
              </svg>
            </button>
          </div>
        </div>
        <div className="modal-body">
          <div className="text-center">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="right-modal">
      {/* Modal Header */}
      <div className="modal-header">
        <div className="modal-title">
          <h2>Refund Details</h2>
          <button className="modal-close-btn" onClick={onClose}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-x h-5 w-5"
            >
              <path d="M18 6 6 18"></path>
              <path d="m6 6 12 12"></path>
            </svg>
          </button>
        </div>
      </div>

      {/* Modal Body */}
      <div className="modal-body">
        {/* Request Info */}
        <div className="requested-info">
          <span className="request-name">
            Requested on{" "}
            {moment(
              selectedIssueResolution?.cancellationDetails?.cancelledOn
            ).format("DD-MM-YYYY")}{" "}
            at{" "}
            {moment(
              selectedIssueResolution?.cancellationDetails?.cancelledOn
            ).format("HH:mm")}
          </span>
          {statusData && (
            <span className={`badge text-capitalize ${statusData.badge}`}>
              {statusData.icon}
              <span className="ml-1">{statusData.label}</span>
            </span>
          )}
        </div>

        {/* Refund Info */}
        <div className="summary-box">
          <div>
            <span className="sum-title">Refund Amount</span>
            <span className="amount">
              £{selectedIssueResolution?.jobDetails?.bookedFor / 100 || "0.00"}
            </span>
          </div>
          <div>
            <span className="sum-title">Reason</span>
            <span className="sum-description">
              {selectedIssueResolution?.cancellationDetails?.reason ||
                "No reason provided"}
            </span>
          </div>
          <div>
            <span className="sum-title">Stripe Transaction ID</span>
            {selectedIssueResolution?.jobDetails?.stripeChargeId ? (
              <a
                href={`https://dashboard.stripe.com/payments/${selectedIssueResolution.jobDetails.stripeChargeId}`}
                target="_blank"
                className="anchor-primary-link"
                rel="noopener noreferrer"
              >
                {selectedIssueResolution.jobDetails.stripeChargeId}
              </a>
            ) : (
              <span>Not available</span>
            )}
          </div>
        </div>

        {/* Job Details */}
        <div className="job-info">
          <div className="job-header">
            <div>
              <h3>
                <strong>
                  {getJobTypeDisplay(
                    selectedIssueResolution?.jobDetails?.jobType
                  )}
                </strong>
              </h3>
              <div className="d-flex align-items-center g-8">
                <span className="job-code">
                  {selectedIssueResolution?.jobDetails?.shortId}
                </span>
                <span className="dot"></span>
                {jobStatusData && (
                  <span
                    className={`badge text-capitalize ${jobStatusData.badge}`}
                  >
                    {jobStatusData.icon}
                    <span className="ml-1">{jobStatusData.label}</span>
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={(e) =>
                toScreen(
                  e,
                  navigate,
                  "/job/" + selectedIssueResolution?.jobDetails?.shortId
                )
              }
              className="btn btn-secondary"
            >
              View Job Details
            </button>
          </div>

          {/* Job Images */}
          {selectedIssueResolution?.jobDetails?.images?.length > 0 && (
            <div className="job-images">
              {selectedIssueResolution.jobDetails.images.map((image, index) => (
                <div className="job-image" key={index}>
                  <img
                    src={getJobDetailsSrc(
                      selectedIssueResolution.jobDetails.shortId,
                      image
                    )}
                    alt={`Job image ${index + 1}`}
                    className="c-avatar__pic"
                    onError={(e) => {
                      e.target.src = DefaultImage;
                    }}
                  />
                </div>
              ))}
            </div>
          )}

          <div className="job-locations">
            <div>
              <span className="modal-label">From</span>
              <span>
                {selectedIssueResolution?.jobDetails?.from || "Not specified"}
              </span>
            </div>
            <div>
              <span className="modal-label">To</span>
              <span>
                {selectedIssueResolution?.jobDetails?.to || "Not specified"}
              </span>
            </div>
          </div>

          <div className="job-dates">
            <div>
              <span className="modal-label">Posted on</span>
              <span>
                {selectedIssueResolution?.jobDetails?.creationDate
                  ? moment(
                      selectedIssueResolution.jobDetails.creationDate
                    ).format("DD-MM-YYYY")
                  : "Not available"}
              </span>
            </div>
            <div>
              <span className="modal-label">Booked on</span>
              <span>
                {selectedIssueResolution?.jobDetails?.bookedOn
                  ? moment(selectedIssueResolution.jobDetails.bookedOn).format(
                      "DD-MM-YYYY"
                    )
                  : "Not available"}
              </span>
            </div>
          </div>
        </div>

        {/* Client & Driver Cards */}
        <div className="profiles">
          {/* Client Profile */}
          <div className="profile-box">
            <strong>Client</strong>
            <div className="profile">
              <img
                src={getClientAvatarSrc(selectedIssueResolution)}
                alt="Client avatar"
                className="c-avatar__pic u-cursor-pointer"
                onError={(e) => {
                  e.target.src = AvatarImg;
                }}
              />
              <div>
                {selectedIssueResolution?.userDetails?.firstname ||
                  "Unknown Client"}
                <small>
                  ID: {selectedIssueResolution?.userDetails?.shortId || "N/A"}
                </small>
              </div>
            </div>
            <div className="profile-actions">
              <button
                onClick={(e) =>
                  toScreen(
                    e,
                    navigate,
                    "/client/" + selectedIssueResolution?.userDetails?.shortId
                  )
                }
                disabled={!selectedIssueResolution?.userDetails?.shortId}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                Profile
              </button>
              <button
                className="c-btn c-btn--small c-btn--full-width c-btn--green"
                onClick={(e) =>
                  toScreen(
                    e,
                    navigate,
                    "/message/" + selectedIssueResolution?.userDetails?.shortId,
                    false,
                    {
                      receiverId: selectedIssueResolution?.userDetails?._id,
                      receiverShortId:
                        selectedIssueResolution?.userDetails?.shortId,
                      receiverType: "user",
                      userShortId1: "admin",
                      userShortId2:
                        selectedIssueResolution?.userDetails?.shortId,
                      username2:
                        selectedIssueResolution?.userDetails?.accountInfo
                          ?.username,
                      selectedPage:
                        window.history.state?.usr?.selectedPage ?? 1,
                      selectedFilter:
                        window.history.state?.usr?.selectedFilter ??
                        defaultMessagesFilter,
                      previousPath:
                        window.history.state?.usr?.previousPath ??
                        window.location.pathname,
                    }
                  )
                }
                disabled={!selectedIssueResolution?.userDetails?.shortId}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"></path>
                </svg>
                Chat
              </button>
            </div>
          </div>

          {/* Driver Profile */}
          <div className="profile-box">
            <strong>Driver</strong>
            <div className="profile">
              <img
                src={getDriverAvatarSrc(selectedIssueResolution)}
                alt="Driver avatar"
                className="c-avatar__pic u-cursor-pointer"
                onError={(e) => {
                  e.target.src = AvatarImg;
                }}
              />
              <div>
                {selectedIssueResolution?.driverDetails?.firstname ||
                  "Unknown Driver"}
                <small>
                  ID: {selectedIssueResolution?.driverDetails?.shortId || "N/A"}
                </small>
              </div>
            </div>
            <div className="profile-actions">
              <button
                onClick={(e) =>
                  toScreen(
                    e,
                    navigate,
                    "/mav/" + selectedIssueResolution?.driverDetails?.shortId
                  )
                }
                disabled={!selectedIssueResolution?.driverDetails?.shortId}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                Profile
              </button>
              <button
                className="c-btn c-btn--small c-btn--full-width c-btn--green"
                onClick={(e) =>
                  toScreen(
                    e,
                    navigate,
                    "/message/" +
                      selectedIssueResolution?.driverDetails?.shortId,
                    false,
                    {
                      receiverId: selectedIssueResolution?.driverDetails?._id,
                      receiverShortId:
                        selectedIssueResolution?.driverDetails?.shortId,
                      receiverType: "user",
                      userShortId1: "admin",
                      userShortId2:
                        selectedIssueResolution?.driverDetails?.shortId,
                      username2:
                        selectedIssueResolution?.driverDetails?.firstname,
                      selectedPage:
                        window.history.state?.usr?.selectedPage ?? 1,
                      selectedFilter:
                        window.history.state?.usr?.selectedFilter ??
                        defaultMessagesFilter,
                      previousPath:
                        window.history.state?.usr?.previousPath ??
                        window.location.pathname,
                    }
                  )
                }
                disabled={!selectedIssueResolution?.driverDetails?.shortId}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"></path>
                </svg>
                Chat
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="chat-tabs">
          <div
            className={`chat-tab${activeTab === "chat" ? " active" : ""}`}
            onClick={() => {
              setActiveTab("chat");
              getConversations(selectedIssueResolution);
            }}
            tabIndex={0}
            role="button"
            aria-pressed={activeTab === "chat"}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
            Client-Driver Chat
          </div>
          <div
            className={`chat-tab${activeTab === "notes" ? " active" : ""}`}
            onClick={() => {
              setActiveTab("notes");
              handleGetJobNotes();
            }}
            tabIndex={0}
            role="button"
            aria-pressed={activeTab === "notes"}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
              <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
              <path d="M10 9H8"></path>
              <path d="M16 13H8"></path>
              <path d="M16 17H8"></path>
            </svg>
            Job Notes
          </div>
        </div>
        {/* Chat Tab */}
        {activeTab === "chat" && (
          <div className="chat-history">
            {conversationDetails?.messages?.map((messageDetails, index) => {
              const userType =
                messageDetails?.userType === "mav" ? "driver" : "client";
              return (
                <div key={index} className={`chat-message ${userType}`}>
                  <div class="text-sm mb-4 text-capitalize">{userType}</div>
                  <div className="text">{messageDetails?.message}</div>
                  <div className="meta">
                    {messageDetails?.time} ,{messageDetails?.date}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {/* Notes Tab */}
        {activeTab === "notes" && (
          <div className="job-notes">
            {notes?.map((note, idx) => (
              <div className="note-item" key={idx}>
                <div className="note-header">
                  <div className="author text-capitalize">{"Admin"}</div>
                  <div className="timestamp">
                    {" "}
                    {new Date(note?.createdAt).toLocaleString()}
                  </div>
                </div>
                <div className="note-body text-sm">{note?.content}</div>
              </div>
            ))}
            <div className="add-note">
              <label htmlFor="add-note-textarea">Add a note</label>
              <textarea
                id="add-note-textarea"
                placeholder="Type your note here..."
                value={noteInput}
                onChange={(e) => setNoteInput(e.target.value)}
                aria-label="Add note"
              ></textarea>
              <button
                className="btn add-btn"
                onClick={() =>
                  handleAddJobNotes({
                    id: selectedIssueResolution?._id,
                    noteType: "job",
                    content: noteInput,
                  })
                }
              >
                Add Note
              </button>
            </div>
          </div>
        )}
      </div>
      {/* Modal Footer */}
      <div className="modal-footer">
        <button className="btn btn-outline-primary" onClick={onClose}>
          Close
        </button>
        {selectedIssueResolution?.refund === "pending" && (
          <div>
            <button
              className="btn btn-danger"
              style={{ marginRight: 12 }}
              onClick={() => {
                setIsDecisionMade(true);
                handleFetchRefundPayment({
                  paymentChargeId:
                    selectedIssueResolution?.jobDetails?.stripeChargeId,
                  decision: "rejected",
                });
              }}
            >
              Reject Refund
            </button>
            <button
              className="btn btn-success"
              onClick={() => {
                setIsDecisionMade(true);
                handleFetchRefundPayment({
                  paymentChargeId:
                    selectedIssueResolution?.jobDetails?.stripeChargeId,
                  decision: "approved",
                });
              }}
            >
              Approve Refund
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RefundDetails;
